import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import type { SessionUser } from 'models/auth/SessionUser';
import AppShell, { NavItem } from './AppShell';
import { clearAccessToken } from 'models/auth/accessTokenStore';
import { Team } from 'models/teams/Team';
import ArtistTeamTutorialModal from 'components/onboarding/ArtistTeamTutorialModal';
import { isArtistTutorialDismissed } from 'utils/tutorialPrefs';

type Props = {
  children: React.ReactNode;
};

const AuthenticatedShell: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialClosedForSession, setTutorialClosedForSession] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionUser = await ApiService.getMe();
        setUser(sessionUser);

        // Use session payload as an immediate signal for team membership.
        if (sessionUser.teams?.length) {
          setTeams(sessionUser.teams);
        }

        // Load teams (authoritative list)
        if (sessionUser.teams && sessionUser.teams.length > 0) {
          const teamResponses = await ApiService.getUserTeams('');

          if (teamResponses.success && teamResponses.data) {
            const teamData = teamResponses.data.map((response) => response as Team);
            setTeams(teamData);
          }
        }
      } catch (e) {
        console.error('Failed to bootstrap session', e);
        setError('Failed to load session');
        clearAccessToken();
        localStorage.removeItem('userId');
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    void bootstrap();
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (!user) return;
    if (tutorialClosedForSession) return;

    // Show tutorial for any authenticated user with at least one team.
    if (teams.length > 0 && !isArtistTutorialDismissed()) {
      setTutorialOpen(true);
    }
  }, [teams.length, user, tutorialClosedForSession]);

  const homeHref = useMemo(() => {
    return '/home';
    //return user.isArtist && user.artistId ? `/artist/${user.artistId}` : '/home';
  }, [user]);

  const navItems = useMemo<NavItem[]>(() => {
    if (!user) return [];

    const items: NavItem[] = [{ label: 'Home', to: '/home' }];

    // Teams navigation (available for any user with team membership)
    if (teams.length === 1) {
      const team = teams[0];
      items.push({
        label: 'Teams',
        to: `/artist/${team.artistId}/teams/${team.teamId}`,
      });
    } else if (teams.length > 1) {
      items.push({
        label: 'Teams',
        children: teams.map((team) => ({
          label: team.teamName,
          to: `/artist/${team.artistId}/teams/${team.teamId}`,
        })),
      });
    }

    // Artist Page link
    if (user.isArtist && user.artistId) {
      items.push({ label: 'Artist Page', to: `/artist/${user.artistId}` });
    }

    return items;
  }, [user, teams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading...
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error || 'Unauthorized'}
      </div>
    );
  }

  return (
    <AppShell user={user} homeHref={homeHref} navItems={navItems}>
      <ArtistTeamTutorialModal
        isOpen={tutorialOpen}
        teamsHref={teams.length ? `/artist/${teams[0].artistId}/teams/${teams[0].teamId}` : homeHref}
        onClose={() => {
          setTutorialOpen(false);
          setTutorialClosedForSession(true);
        }}
      />
      {children}
    </AppShell>
  );
};

export default AuthenticatedShell;
